import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth'
import { doc, onSnapshot, terminate, clearIndexedDbPersistence } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { auth, db, functions, googleProvider, appleProvider } from '@/lib/firebase'
import { COLLECTIONS } from '@/lib/collections'
import { createUserProfile } from '@/lib/firestore/profile'
import { identifyUser } from '@/lib/analytics'
import type { UserProfile } from '@/types'

/**
 * Account setup state for a signed-in user with no ksnap_users doc yet:
 *  - 'idle'      → profile exists (or nothing to do)
 *  - 'running'   → legacy migration + profile creation in flight
 *  - 'failed'    → setup failed; the UI offers retry
 */
export type SetupState = 'idle' | 'running' | 'failed'

interface AuthContextValue {
  user: User | null
  userProfile: UserProfile | null
  /** True until initial auth resolves. */
  loading: boolean
  profileLoading: boolean
  /**
   * Effective premium — mirrors mobile usePaywall().hasPremium as closely as
   * the web can: profile isPremium (kept fresh by the RevenueCat webhook +
   * purchases) OR the server-only legacyPremium grandfather flag.
   */
  hasPremium: boolean
  /** Legacy-data migration / first-profile setup progress. */
  setupState: SetupState
  retrySetup: () => void
  signInEmail: (email: string, password: string) => Promise<void>
  signUpEmail: (email: string, password: string) => Promise<void>
  signInGoogle: () => Promise<void>
  signInApple: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

// Long-tenured legacy accounts can take a while server-side (the function
// runs with timeoutSeconds: 540) — give the client call matching headroom.
const migrateLegacyUserFn = httpsCallable(functions, 'migrateLegacyUser', { timeout: 540_000 })

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  // Set to the uid once the profile subscription has delivered a SERVER-
  // confirmed answer for that user — a cached miss must never trigger setup.
  const [profileResolvedUid, setProfileResolvedUid] = useState<string | null>(null)
  const [setupState, setSetupState] = useState<SetupState>('idle')
  const [setupNonce, setSetupNonce] = useState(0)

  // Auth state.
  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u)
      identifyUser(u ? u.uid : null)
      setAuthLoading(false)
      if (!u) {
        setUserProfile(null)
        setProfileResolvedUid(null)
        setSetupState('idle')
      }
    })
  }, [])

  // Real-time user profile (mirrors mobile AuthContext profile subscription).
  useEffect(() => {
    if (!user) return
    const uid = user.uid
    setProfileLoading(true)
    setProfileResolvedUid(null)
    const ref = doc(db, COLLECTIONS.USERS, uid)
    const unsub = onSnapshot(
      ref,
      { includeMetadataChanges: true },
      (snap) => {
        if (snap.exists()) {
          const data = snap.data()
          setUserProfile({
            ...data,
            id: snap.id,
            createdAt: data.createdAt?.toDate?.(),
            updatedAt: data.updatedAt?.toDate?.(),
          } as UserProfile)
          setProfileResolvedUid(uid)
        } else if (!snap.metadata.fromCache) {
          // Server-confirmed: this account really has no profile yet.
          setUserProfile(null)
          setProfileResolvedUid(uid)
        }
        // Cached miss → not authoritative; keep waiting for the server result.
        setProfileLoading(false)
      },
      (err) => {
        // Listener error (offline/rules blip): never conclude "no account".
        console.error('Profile subscription failed:', err)
        setProfileLoading(false)
      },
    )
    return unsub
  }, [user])

  // Signed in with a server-confirmed missing profile → run first-time setup:
  // 1. migrateLegacyUser (idempotent; no-op 'not_legacy' for fresh accounts;
  //    for pre-v10 accounts it moves RTDB userdata → ksnap_* and writes the
  //    profile doc LAST, so a completed migration implies the profile exists).
  // 2. If still no profile (not a legacy user), merge-create the default one.
  const profileMissing = !!user && profileResolvedUid === user.uid && userProfile === null
  useEffect(() => {
    if (!profileMissing || !user) return
    if (setupState === 'failed') return // wait for explicit retry
    let cancelled = false
    setSetupState('running')
    ;(async () => {
      try {
        await migrateLegacyUserFn({})
      } catch (err) {
        // Migration failing for a REAL legacy user must not strand them with a
        // blank default profile — surface retry instead of pressing on.
        console.error('migrateLegacyUser failed:', err)
        if (!cancelled) setSetupState('failed')
        return
      }
      try {
        // 'not_legacy' (or an already-migrated account whose doc was deleted):
        // the snapshot listener still reports missing → create the default.
        await createUserProfile(user.uid)
        if (!cancelled) setSetupState('idle')
      } catch (err) {
        console.error('createUserProfile failed:', err)
        if (!cancelled) setSetupState('failed')
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setupNonce re-arms retry
  }, [profileMissing, user, setupNonce])

  const retrySetup = useCallback(() => {
    setSetupState('idle')
    setSetupNonce((n) => n + 1)
  }, [])

  const hasPremium = userProfile?.isPremium === true || userProfile?.legacyPremium === true

  const value: AuthContextValue = {
    user,
    userProfile,
    loading: authLoading,
    profileLoading,
    hasPremium,
    setupState,
    retrySetup,
    signInEmail: async (email, password) => {
      await signInWithEmailAndPassword(auth, email, password)
    },
    signUpEmail: async (email, password) => {
      await createUserWithEmailAndPassword(auth, email, password)
      // Profile creation happens via the profileMissing effect (which also
      // catches legacy accounts that predate this email existing in ksnap_).
    },
    signInGoogle: async () => {
      await signInWithPopup(auth, googleProvider)
    },
    signInApple: async () => {
      await signInWithPopup(auth, appleProvider)
    },
    resetPassword: async (email) => {
      await sendPasswordResetEmail(auth, email)
    },
    signOut: async () => {
      await fbSignOut(auth)
      // Shared-computer hygiene: the persistent cache holds health data on
      // disk. Terminate + clear, then hard-reload into a clean session. If
      // another tab holds the DB the clear throws — data stays uid-scoped
      // and unreadable in-app, so signing out still succeeds.
      try {
        await terminate(db)
        await clearIndexedDbPersistence(db)
      } catch {
        /* multi-tab or already terminated */
      }
      window.location.replace('/sign-in')
    },
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
